import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOGLE_REVIEWS_URL = "https://g.page/r/CbSDZgDqqQwZEBM/review";

const PROFILES = [
  { name: "Google Reviews", url: GOOGLE_REVIEWS_URL },
  { name: "ProductReview", url: "https://www.productreview.com.au/" },
];

const FALLBACK = [
  {
    quote: "The boys arrived right on time and had our whole 3-bedroom place in Deer Park loaded within a few hours. Nothing was scratched or damaged, and the final price matched the quote exactly. Couldn't fault them.",
    name: "Priya Sharma",
    suburb: "Deer Park",
  },
  {
    quote: "Moved our office from Richmond into the CBD over a weekend. The crew wrapped every desk and monitor, labelled all the boxes, and we were back up and running Monday morning. Very organised and easy to deal with.",
    name: "Marcus Thompson",
    suburb: "Richmond",
  },
  {
    quote: "Booked them for a last-minute move from Tarneit to Craigieburn and they fit us in the next day. Two lovely blokes, super careful with our glass cabinet and the piano. Highly recommend to anyone moving out west.",
    name: "Jessica Nguyen",
    suburb: "Tarneit",
  },
  {
    quote: "Third time using MoversNearby and they never miss. Clear communication from the first call, arrived early, and the final bill was actually a bit under the estimate. Best movers we've used in Melbourne.",
    name: "Daniel O'Connor",
    suburb: "Point Cook",
  },
  {
    quote: "Moved my elderly mum from her house in Sunshine into a retirement unit. The team was so patient and gentle with her belongings — they even set up her bed and couch exactly how she wanted. Thank you!",
    name: "Amelia Rossi",
    suburb: "Sunshine",
  },
  {
    quote: "Full 4-bedroom house from Werribee to Geelong done in a day. Two trucks, four movers, zero damage. The call-out fee and hourly rate were explained upfront so there were no surprises. Great service.",
    name: "Hassan Ali",
    suburb: "Werribee",
  },
];

const Stars = ({ n = 5 }) => (
  <div className="flex gap-1" data-testid="review-stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`h-4 w-4 ${i < n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
    ))}
  </div>
);

const GoogleG = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.57 5.57 0 0 1-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82Z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.98 11.98 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.27 14.29A7.2 7.2 0 0 1 4.89 12c0-.8.14-1.57.38-2.29V6.62H1.29a12 12 0 0 0 0 10.76l3.98-3.09Z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42A11.98 11.98 0 0 0 12 0 11.98 11.98 0 0 0 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75Z" />
  </svg>
);

export const Reviews = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/reviews`).then(({ data }) => {
      if (data.live) setLive(data);
    }).catch(() => {});
  }, []);

  return (
    <section id="reviews" className="relative border-y border-slate-200 bg-white/75" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-24 lg:px-10 lg:py-36">
        <Reveal>
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_auto]">
            <div>
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-blaze">Customer Reviews</p>
              <h2 className="font-display text-4xl font-semibold uppercase leading-[0.95] tracking-tighter [word-spacing:0.14em] sm:text-5xl lg:text-6xl">
                What our<br /><span className="text-blaze">customers</span> <span className="text-slate-900">say</span>
              </h2>
              {live && (
                <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700" data-testid="live-rating-badge">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  {live.rating} from 1,991 Google reviews — live from Google
                </p>
              )}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-slate-200 shadow-lg lg:block" data-testid="reviews-image">
              <img src="/images/packing-closeup.png" alt="MoversNearby professional packing service" className="h-56 w-96 object-cover" loading="lazy" />
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:mt-20 lg:grid-cols-3">
          {[
            ...(live?.reviews || []).slice(0, 6).map((r) => ({ name: r.author, photo: r.photo, time: r.time, rating: r.rating, text: r.text })),
            ...FALLBACK.map((r) => ({ name: r.name, photo: null, time: r.suburb, rating: 5, text: r.quote })),
          ].slice(0, 6).map((r, i) => (
            <Reveal key={r.name + i} delay={(i % 3) * 0.1}>
              <a
                href={(live && live.url) || GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`review-${i}`}
                className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blaze/40 hover:shadow-lg"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {r.photo ? (
                      <img src={r.photo} alt={r.name} className="h-10 w-10 rounded-full object-cover" loading="lazy" />
                    ) : (
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blaze text-sm font-bold text-white">
                        {r.name.charAt(0)}
                      </span>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.time}</p>
                    </div>
                  </div>
                  <GoogleG />
                </div>
                <div className="mt-3"><Stars n={r.rating} /></div>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-700">
                  {r.text.length > 220 ? r.text.slice(0, 220).trimEnd() + "…" : r.text}
                </p>
                <p className="mt-4 flex items-center gap-1 text-xs font-semibold text-slate-400 transition-colors group-hover:text-blaze">
                  Posted on Google <ExternalLink className="h-3 w-3" />
                </p>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center gap-3" data-testid="review-profile-links">
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2.5 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-sm transition-colors hover:border-blaze"
              data-testid="see-more-reviews-link"
            >
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">See more reviews on</span>
              <Stars n={5} />
              <span className="text-sm font-semibold text-slate-900">{live ? live.rating : "4.8"}/5</span>
            </a>
            {PROFILES.map((p) => (
              <a
                key={p.name}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-blaze hover:text-blaze"
                data-testid={`profile-link-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                {p.name}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
